import { P, R, SharedColors } from "apparatus/library/common";
import { Interactable } from "editing/interaction";
import { clog } from "log";
import { Group, Point, PointText, Shape } from "paper";
const DEBUG_LOGS = false;
const DEBUG_CURSOR = false;
/** Spacing between lines, as a multiple of font size. */
const LINE_SPACING = 1.25;
/**
 * Class for rendering advanced text.
 *
 * Underneath, it opaquely uses Paper.js's PointText and Groups.
 * For performance reasons, these may be reused. The internal
 * paper structure should not be messed with.
 */
export class AdvancedText {
    constructor(container) {
        this.container = new Group();
        this.localAlignmentBounds = R(0, 0, 0, 0);
        this.pointTextRecycler = new PointTextRecycler();
        this.container = container !== null && container !== void 0 ? container : new Group();
        this.container.applyMatrix = false;
        this.container.selectedColor = SharedColors.selectionGreen;
    }
    /** Constructor that allows the container to be interactable. */
    static withInteraction(interaction) {
        const interactableGroupClass = Interactable(Group, interaction);
        return new AdvancedText(new interactableGroupClass());
    }
    setText(text, style) {
        // First recycle all current items.
        this.pointTextRecycler.recycle(...this.container.children);
        // Empty text leads to rendering bugs. If the text has no segments, render
        // a space.
        if (text.segments.length == 0) {
            text.segments.push({ type: "normal", text: " " });
        }
        if (text.segments.length == 1 && text.segments[0].type == "normal" && text.segments[0].text == "") {
            text.segments[0].text = " ";
        }
        // Convert style to internal style.
        const internalStyle = Object.assign(Object.assign({}, style), { justification: "left" });
        // Set up tracking of lines so that we can do text align properly at the end.
        let lines = [];
        let maxLineWidth = 0;
        let cursor = P(0, 0);
        for (let segment of text.segments) {
            if (DEBUG_CURSOR) {
                const point = Shape.Circle(cursor, 5.0);
                point.fillColor = "#ff0000";
                this.container.addChild(point);
            }
            if (segment.type == "newline") {
                const lineWidth = cursor.x;
                lines.push([this.container.children.length, lineWidth]);
                maxLineWidth = Math.max(maxLineWidth, lineWidth);
            }
            let items = renderSegment(segment, cursor, this.pointTextRecycler, internalStyle);
            if (items.length > 0) {
                // Check length as newlines don't return anything.
                this.container.addChildren(items);
            }
        }
        // Track the last line.
        const lineWidth = cursor.x;
        lines.push([this.container.children.length, lineWidth]);
        maxLineWidth = Math.max(maxLineWidth, lineWidth);
        // Iterate through children and align each line to match alignment.
        if (style.textAlign == "left" || lines.length == 1) {
            // Nothing to do, by default, we left-align. Also if the text
            // consists of 1 line, nothing to do either.
        }
        else {
            var i = 0;
            for (const [maxIndex, lineWidth] of lines) {
                // If this is the longest line, nothing to do.
                if (lineWidth == maxLineWidth) {
                    i = maxIndex;
                    continue;
                }
                // Compute gap.
                let gap = maxLineWidth - lineWidth;
                if (style.textAlign == "center")
                    gap /= 2.0;
                // Shift all elements on the line by the gap.
                while (i < maxIndex) {
                    this.container.children[i].position.x += gap;
                    i++;
                }
            }
        }
        // Pre-compute alignment bounds.
        // For line height, don't quite take the full font-size.
        const lineHeight = style.fontSize * 0.7;
        const lineSpacing = LINE_SPACING * style.fontSize;
        this.localAlignmentBounds = R(0, -lineHeight, maxLineWidth, lineHeight + (lines.length - 1) * lineSpacing);
    }
    setPosition(x, y) {
        this.container.position = new Point(x, y);
    }
    get bounds() {
        return this.container.bounds;
    }
    get alignmentBounds() {
        // Return rectangle for text, ignoring subscripts etc.
        const origin = this.container.localToGlobal(P(0, 0));
        const alignmentBounds = this.localAlignmentBounds.clone();
        alignmentBounds.point.x += origin.x;
        alignmentBounds.point.y += origin.y;
        return alignmentBounds;
    }
    remove() {
        this.pointTextRecycler.dispose();
        this.container.remove();
    }
}
/**
 * Recycles PointText instances. To put stale instances into the recycle,
 * use recycle(). To request a recycled item, use request(). If the recycler
 * has run out of recycled instances, a new instance is returned.
 *
 * Note recycled instances are returned without modification and so all
 * relevant properties must be set of them during use.
 */
class PointTextRecycler {
    constructor() {
        this.recycled = [];
    }
    recycle(...items) {
        for (const item of items) {
            item.remove();
        }
        this.recycled.push(...items);
    }
    request(position) {
        // REMEMBER: Set position only once, before text is set!
        let item = this.recycled.pop();
        if (item) {
            item.pivot = P(0, 0);
            item.position = position;
            return item;
        }
        let newInstance = new PointText(position);
        // By setting the pivot we ensure the set
        // position does not move around.
        newInstance.pivot = position;
        newInstance.selectedColor = SharedColors.selectionGreen;
        return newInstance;
    }
    dispose() {
        for (let recycled of this.recycled) {
            recycled.remove();
        }
        this.recycled.length = 0;
    }
}
/**
 * Renders a segment, returning items.
 * @param cursor Position to render at. Must be updated after rendering.
 */
function renderSegment(segment, cursor, pointTextRecycler, style) {
    const fontSize = style.fontSize;
    switch (segment.type) {
        case "normal":
        case "subscript":
        case "superscript":
            let pointText = pointTextRecycler.request(cursor);
            if (DEBUG_LOGS)
                clog("before: ", pointText.position);
            pointText.content = segment.text;
            pointText.style = style;
            pointText.fontSize = fontSize;
            if (DEBUG_LOGS)
                clog("after: ", pointText.position);
            if (segment.type == "subscript") {
                formatSubscript(pointText, style);
            }
            else if (segment.type == "superscript") {
                formatSuperscript(pointText, style);
            }
            cursor.x += pointText.bounds.width;
            return [pointText];
        case "subsuperscript":
            const superText = pointTextRecycler.request(cursor);
            superText.content = segment.super;
            const subText = pointTextRecycler.request(cursor);
            subText.content = segment.sub;
            formatSuperscript(superText, style);
            formatSubscript(subText, style);
            cursor.x += Math.max(superText.bounds.width, subText.bounds.width);
            return [subText, superText];
        case "newline":
            cursor.x = 0;
            cursor.y += style.fontSize * LINE_SPACING;
            return [];
    }
}
function formatSubscript(text, style) {
    text.style = style;
    text.fontSize = style.fontSize * 0.75;
    text.position.y += style.fontSize * 0.27;
}
function formatSuperscript(text, style) {
    text.style = style;
    text.fontSize = style.fontSize * 0.75;
    text.position.y -= style.fontSize * 0.42;
}
